import React, { useEffect, useState } from "react";
import { Table, Button, message, Tag, DatePicker, Select, Space } from "antd";
import axios from "axios";
import config from "../config";
import "./TabComponent.css"; // Add this import
import Swal from 'sweetalert2';
const { RangePicker } = DatePicker;
const { Option } = Select;


const TabComponent = () => {
  const apiBaseUrl = config.REACT_APP_API_BASE_URL;
  const [dataSource, setDataSource] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateRange, setDateRange] = useState([]);
  const [consolidatedFilter, setConsolidatedFilter] = useState("all"); // Add state for consolidated filter
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });


  const handleFetch = async (force = false) => {
    if (!force && (!dateRange || dateRange.length !== 2)) {
      Swal.fire({
        icon: "warning",
        title: "Please select a valid date range.",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
      });
      return;
    }
    try {
      const data = await fetchData(force);
      setDataSource(data);
    } catch (error) {
      console.error("Error fetching data:", error);
      Swal.fire({
        icon: "error",
        title: "Failed to fetch data. Please try again later.",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
      });
    }
  };


  const fetchData = async (force = false) => {
    setLoading(true);
    try {
      const params = {};
      if (!force && dateRange.length === 2) {
        params.startDate = dateRange[0].toISOString();
        params.endDate = dateRange[1].toISOString();
      }
      if (statusFilter !== "all") {
        params.status = statusFilter;
      }
      params.isConsolidated = consolidatedFilter

      const response = await axios.get(`${apiBaseUrl}/api/payments/with-einvoice`, { params });
      const data = response.data;
      if (data.length === 0) {
        Swal.fire({
          icon: "warning",
          title: "No data found for the selected filters.",
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 3000,
        });
        return [];
      }
      return data.map((item) => ({
        ...item,
        key: item._id,
      }));
    } catch (error) {
      console.error("Failed to fetch data:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const data = await fetchData();
        setDataSource(data);
      } catch (error) {
        console.error("Failed to fetch data:", error);
      } finally {
        setLoading(false);
      }
    };

    loadData();


  }, [dateRange, statusFilter,consolidatedFilter]);
  const handleSubmit = async () => {
    if (selectedRowKeys.length === 0) {
      Swal.fire({
        icon: "warning",
        title: "Please select at least one invoice.",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
      });
      return;
    }

    try {
      const response = await axios.post(`${apiBaseUrl}/api/einvoices/submit-all-invoice`, {
        invoiceIds: selectedRowKeys,
      });
      const data = response.data;
      // Check for errors in the response
      let errorMsg = null;
      if (data.error) {
        errorMsg = data.error;
      } else if (data.normalInvoices && Array.isArray(data.normalInvoices)) {
        const failed = data.normalInvoices.filter(inv => inv.error);
        if (failed.length > 0) {
          errorMsg = failed.map(inv => `Invoice ${inv.invoiceId}: ${inv.error}`).join("; ");
        }
      }
      if (data.consolidatedInvoice && data.consolidatedInvoice.error) {
        errorMsg = (errorMsg ? errorMsg + '; ' : '') + `Consolidated: ${data.consolidatedInvoice.error}`;
      }
      if (errorMsg) {
        Swal.fire({
          icon: "error",
          title: errorMsg,
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 3000,
        });
      } else {
        Swal.fire({
          icon: "success",
          title: "Invoices submitted successfully!",
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 3000,
        });
        console.log(data);
      }
    } catch (error) {
      // Handle different types of errors
      let errorMessage = 'An unexpected error occurred';
      
      if (error.response) {
        // Server responded with error status (400, 500, etc.)
        const errorData = error.response.data;
        if (errorData && errorData.error) {
          errorMessage = errorData.error;
        } else if (errorData && typeof errorData === 'string') {
          errorMessage = errorData;
        } else {
          errorMessage = `Request failed with status code ${error.response.status}`;
        }
      } else if (error.request) {
        // Network error
        errorMessage = 'Network error - please check your connection';
      } else {
        // Other errors
        errorMessage = error.message || 'An unexpected error occurred';
      }

      Swal.fire({
        icon: "error",
        title: errorMessage,
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
      });
    }
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys) => setSelectedRowKeys(keys),
  };



  const columns = [
    { title: "Payment ID", dataIndex: "_id", key: "_id", sorter: (a, b) => a._id.localeCompare(b._id) },
    { title: "Invoice ID", dataIndex: "invoiceId", key: "invoiceId", sorter: (a, b) => a.invoiceId.localeCompare(b.invoiceId) },
    { title: "Date", dataIndex: "date", key: "date" ,       render: (date) => {
        const d = new Date(date);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}/${month}/${day}`;
      } },
      {
        title: "Consolidated Invoice",
        dataIndex: "einvoice",
        key: "einvoice",
        render: (einvoice) => einvoice?.[0] ? 
        <Tag color="blue">Not Consolidated</Tag> : <Tag color="green">Consolidated</Tag>,
      },
    {
      title: "Customer Info",
      dataIndex: "einvoice",
      key: "einvoice",
      render: (einvoice) => {
        const customerInfo = einvoice?.[0]?.customerInfo;
        if (!customerInfo) return <Tag color="red">N/A</Tag>;
        const fields = [
          { label: "Registration Name", value: customerInfo.registryName },
          { label: "NRIC Number", value: customerInfo.nricNumber },
          { label: "BRN Number", value: customerInfo.brnNumber },
          { label: "Passport Number", value: customerInfo.passportNumber },
          { label: "armyNumber", value: customerInfo.armyNumber },
          { label: "SST Number", value: customerInfo.sstNumber },
          { label: "TIN", value: customerInfo.tinNumber },
          { label: "Phone", value: customerInfo.phoneNumber },
          { label: "Email", value: customerInfo.email },
          { label: "Address", value: `${customerInfo.addressLine1}, ${customerInfo.cityName}, ${customerInfo.postalCode}` },
          { label: "State", value: customerInfo.stateCode },
          { label: "Country", value: customerInfo.countryCode },
        ];

        return (
          <div>
            {fields.map((field, index) => (
              field.value && <div key={index}><strong>{field.label}:</strong> {field.value}</div>
            ))}
          </div>
        );
      },
    },
   { 
      title: "Status", 
      dataIndex: "eInvoiceSubmissions", 
      key: "status", 
      filters: [
        { text: 'Pending', value: 'PENDING' },
        { text: 'Submitted', value: 'SUBMITTED' },
        { text: 'Accepted', value: 'ACCEPTED' },
        { text: 'Rejected', value: 'REJECTED' },
      ],
      onFilter: (value, record) => {
        if (record.eInvoiceSubmissions && record.eInvoiceSubmissions.length > 0) {
          return record.eInvoiceSubmissions.some(sub => sub.status === value);
        }
        return value === 'PENDING';
      },
      render: (submissions) => (
        submissions?.length > 0 ? (
          <div>
            {submissions.map((submission, index) => {
              let rejectionReason = '';
              if (submission.status === 'REJECTED' && submission.lhdnResponseMessage) {
                try {
                  const response = JSON.parse(submission.lhdnResponseMessage);
                  const resp = response.response || {};
                  const rejected = resp.rejectedDocuments || [];
                  if (rejected.length > 0 && rejected[0].error) {
                    rejectionReason = rejected[0].error;
                  }
                } catch (e) {
                  console.error('Error parsing LHDN response:', e);
                }
              }

              return (
                <div key={index}>
                  <Tag 
                    color={
                      submission.status === 'ACCEPTED' ? 'green' : 
                      submission.status === 'SUBMITTED' ? 'blue' :
                      submission.status === 'PENDING' ? 'orange' : 
                      submission.status === 'REJECTED' ? 'red' : 'default'
                    } 
                  >
                    {submission.status}
                  </Tag>
                  {submission.status === 'REJECTED' && rejectionReason && (
                    <div style={{ fontSize: '12px', color: '#ff4d4f', marginTop: '4px' }}>
                      Reason: {rejectionReason}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : <Tag color="default">Pending</Tag>
      ) 
    },
  ];

  // Date range selection

  const handleDateRangeChange = (dates) => {
    setDateRange(dates);
  };

  const fetchPaymentsWithEInvoices = async () => {
    if (!dateRange || dateRange.length !== 2) {
      alert('Please select a valid date range.');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch('/api/payments/einvoices', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          startDate: dateRange[0].toISOString(),
          endDate: dateRange[1].toISOString(),
        }),
      });
      const data = await response.json();
      setDataSource(data); // Update the table data
    } catch (error) {
      console.error('Failed to fetch payments:', error);
    } finally {
      setLoading(false);
    }
  };

  // Table pagination change handler
  const handleTableChange = (pag) => {
    setPagination({ current: pag.current, pageSize: pag.pageSize });
  };

  // Calculate showing range
  const totalItems = dataSource.length;
  const startItem = totalItems === 0 ? 0 : (pagination.current - 1) * pagination.pageSize + 1;
  const endItem = Math.min(pagination.current * pagination.pageSize, totalItems);

  return (
    <div key="EInvoiceSubmission" className="tab-content">
      <div className="d-flex align-items-center" style={{ justifyContent: 'space-between', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <RangePicker
            className="custom-date-picker"
            onChange={setDateRange}
            style={{ width: 250, height: 39 }}
          />
          <Select
            className="custom-select-with-icon"
            defaultValue="all"
            style={{ width: 150}}
            onChange={(value) => setConsolidatedFilter(value)}
          >
            <Option value="all">All Invoices</Option>
            <Option value="consolidated">Consolidated</Option>
            <Option value="notConsolidated">Not Consolidated</Option>
          </Select>
        </div>
        <Space>
          {/* 
          <Button
            type="primary"
            onClick={handleFetch}
            disabled={!dateRange || dateRange.length !== 2}
          >
            Fetch Invoices
          </Button> 
          */}
          <Button
            type="primary"
            onClick={() => handleFetch(true)}
            loading={loading}
            style={{ marginLeft: 8 }}
          >
            Refresh
          </Button>
          <Button
            type="primary"
            onClick={handleSubmit}
            disabled={selectedRowKeys.length === 0}
            className={`submit-button ${selectedRowKeys.length === 0 ? 'custom-disabled' : ''}`}
          >
            Submit Selected Invoices
          </Button>
        </Space>
      </div>

      {/* Table */}
      <Table
        columns={columns}
        dataSource={dataSource}
        bordered
        loading={loading}
        rowSelection={rowSelection}
        rowKey="_id"
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          showSizeChanger: true,
          pageSizeOptions: ["5", "10", "20", "50"],
          showTotal: (total, range) => total === 0 ? 'No items to show' : `Showing ${range[0]}–${range[1]} of ${total} items`,
        }}
        onChange={handleTableChange}
      />
    </div>
  );
}

export default TabComponent;